/*
 * Jobs and their price lines, visits and the schedule (draft 2).
 *
 * A job belongs to one client and one property. Its lines are what each visit bills: "every" lines come on every visit; an
 * extra can repeat on its own (a monthly window wash on a weekly cleaning). Changing lines "for this and future visits"
 * ends the old lines the day before and starts the new ones, so past visits keep what they were.
 */
import { z } from "zod";
import { listQuery } from "./api.zod.js";
import { dateString, fileRow, noteRow } from "./clients.zod.js";
import { routePathAnswer } from "./field.zod.js";
import { FREQUENCIES, LAST_DAY, LAST_WEEK, REPEAT_UNITS } from "./schedule.js";
import { homeAnswersShape } from "./website.zod.js";
import { lineLink } from "./prices.js";

/**
 * How a job bills: an invoice per visit, every 4 visits, at month end, or outside (paid some other way: its visits make
 * no invoice and stay out of payroll, owner 2026-11-01 for Port Coffee).
 */
export const JOB_BILLINGS = ["visit", "monthly", "monthend", "outside"] as const;
export type JobBilling = (typeof JOB_BILLINGS)[number];
export const BILLING_WORDS: Record<JobBilling, string> = {
  visit: "After each visit",
  monthly: "Once a month",
  monthend: "At month end",
  outside: "Not billed here",
};
/** The words for a job's billing on screen; job answers carry it as a plain string. */
export const billingWords = (b: string): string =>
  (JOB_BILLINGS as readonly string[]).includes(b) ? BILLING_WORDS[b as JobBilling] : b;

const timeString = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use a time like 09:30.");
const optionalText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((s) => (s === "" ? null : s))
    .nullable()
    .optional();

/** once: one visit only (the job's next visit from the day it was added), then it ends on its own. */
export const LINE_FREQUENCIES = [
  "every",
  "once",
  ...FREQUENCIES.filter((f) => f !== "one-time" && f !== "daily" && f !== "custom" && f !== "as-needed"),
] as const;

/** A custom repeat (schedule.ts RepeatRule). */
export const repeatRule = z.object({
  every: z.number().int().min(1).max(99),
  unit: z.enum(REPEAT_UNITS),
  weekdays: z.array(z.number().int().min(1).max(7)).max(7).optional(),
  monthDays: z.array(z.number().int().min(1).max(LAST_DAY)).max(32).optional(),
  monthWeekdays: z
    .array(z.object({ nth: z.number().int().min(1).max(LAST_WEEK), day: z.number().int().min(1).max(7) }))
    .max(35)
    .optional(),
});

export const lineInput = z.object({
  description: z.string().trim().min(1, "Name the line.").max(200),
  note: optionalText(500),
  link: lineLink,
  quantity: z.number().positive("Use a number above 0.").max(100_000).default(1),
  unitPriceCents: z.number().int().min(-10_000_000).max(10_000_000),
  /** Job lines only: every visit, or an extra's own repeat. */
  frequency: z.enum(LINE_FREQUENCIES).default("every"),
  /** Job lines only: the price list answers the line was priced from (the auto quote), so it can be priced again. */
  answers: homeAnswersShape.nullable().optional(),
});
export type LineInput = z.infer<typeof lineInput>;

export const lineRow = z.object({
  id: z.uuid(),
  description: z.string(),
  note: z.string().nullable(),
  quantity: z.number(),
  unitPriceCents: z.number(),
  amountCents: z.number(),
  frequency: z.string(),
  link: z.string().nullable().optional(),
  /** What went into the price, in words ("3 bed, 2 bath, 2 more rooms"): from the answers it was priced from. */
  basis: z.string().nullable().optional(),
  /** A job line priced from the price list: the answers it was priced from. */
  answers: homeAnswersShape.nullable().optional(),
});
export type LineRow = z.infer<typeof lineRow>;

/** photo: the file id of their profile picture (GET people/:id/photo/:fileId), or null for their initials. */
export const personChip = z.object({ id: z.uuid(), name: z.string(), color: z.string().nullable(), photo: z.uuid().nullable() });
export type PersonChip = z.infer<typeof personChip>;

const jobFields = {
  propertyId: z.uuid().nullable().optional(),
  title: optionalText(120),
  service: optionalText(120),
  frequency: z.enum(FREQUENCIES),
  startDate: dateString,
  /** The day it repeats on, 1 Monday to 7 Sunday; null or left out: the start date's weekday. */
  repeatDay: z.number().int().min(1).max(7).nullable().optional(),
  /** The custom repeat, when frequency is custom. */
  repeatRule: repeatRule.nullable().optional(),
  startTime: timeString.nullable().optional(),
  arriveFrom: timeString.nullable().optional(),
  arriveTo: timeString.nullable().optional(),
  hours: z.number().min(0).max(24).nullable().optional(),
  endsOn: dateString.nullable().optional(),
  crew: z.array(z.uuid()).max(20).default([]),
  billing: z.enum(JOB_BILLINGS).default("visit"),
  officeNotes: optionalText(5000),
  crewNotes: optionalText(5000),
  crewNotesEs: optionalText(5000),
  checklistId: z.uuid().nullable().optional(),
};

export const jobInput = z
  .object({
    /** Left out only with a quote: the quote's client, made from its contact when it has none yet. */
    clientId: z.uuid().optional(),
    ...jobFields,
    lines: z.array(lineInput).max(50).default([]),
    /** The client confirms: the job goes on the schedule marked Not confirmed until they sign (and save a card) on the link. */
    askConfirm: z.boolean().optional(),
    /** Made from this approved quote (Make into a job opens New job filled from it). The quote is marked a job. */
    quoteId: z.uuid().optional(),
  })
  .refine((j) => Boolean(j.clientId) || Boolean(j.quoteId), { message: "Pick the client.", path: ["clientId"] })
  .refine((j) => !j.endsOn || j.endsOn >= j.startDate, { message: "The end date is before the start.", path: ["endsOn"] });
export type JobInput = z.infer<typeof jobInput>;

export const jobPatch = z
  .object({
    ...jobFields,
    // No defaults in a change: a field left out stays as it is (a default here wiped the crew and the billing on every
    // partial save, such as End this job or pricing a line again).
    crew: z.array(z.uuid()).max(20),
    billing: z.enum(JOB_BILLINGS),
    frequency: z.enum(FREQUENCIES),
    startDate: dateString,
    active: z.boolean(),
    /** Off: this job is never charged by itself, even when the client has auto charge on. */
    autoPay: z.boolean(),
    lines: z.array(lineInput).max(50),
    /** New lines start on this date (default today); the old ones end the day before. */
    linesFrom: dateString,
    /** A new repeat starts on this day (never before today). Left out: the last visit done plus the new gap. */
    repeatFrom: dateString,
  })
  .partial();
export type JobPatch = z.infer<typeof jobPatch>;

export const jobRow = z.object({
  id: z.uuid(),
  number: z.number().nullable(),
  clientId: z.uuid(),
  clientName: z.string(),
  /** The typed title, else the service, else "Job". */
  name: z.string(),
  frequency: z.string(),
  frequencyLabel: z.string(),
  startDate: z.string().nullable(),
  /** 1 Monday to 7 Sunday, or null: the start date's weekday. */
  repeatDay: z.number().nullable(),
  repeatRule: repeatRule.nullable(),
  /** The repeat counts from here (it was changed from a visit on). */
  repeatFrom: z.string().nullable(),
  startTime: z.string().nullable(),
  endsOn: z.string().nullable(),
  active: z.boolean(),
  address: z.string().nullable(),
  crew: z.array(personChip),
  /** What a usual visit bills (every-visit lines). */
  visitTotalCents: z.number(),
  nextVisit: z.string().nullable(),
  /**
   * A one-time job whose visit is done, with nothing planned after it: the day it was done (owner, 2026-11-02: show it
   * as completed, not Active). Null for repeating jobs and work still ahead.
   */
  completedOn: z.string().nullable().default(null),
});
export type JobRow = z.infer<typeof jobRow>;

export const jobDetail = jobRow.extend({
  title: z.string().nullable(),
  service: z.string().nullable(),
  propertyId: z.uuid().nullable(),
  arriveFrom: z.string().nullable(),
  arriveTo: z.string().nullable(),
  hours: z.number().nullable(),
  billing: z.string(),
  officeNotes: z.string().nullable(),
  crewNotes: z.string().nullable(),
  crewNotesEs: z.string().nullable(),
  checklist: z.object({ id: z.uuid(), name: z.string() }).nullable(),
  /** The last visit done, which a new repeat counts from. */
  lastDone: z.string().nullable(),
  skipDates: z.array(z.string()),
  lines: z.array(lineRow),
  notesList: z.array(noteRow),
  /** A visit a client added in My Trades that clashed with something: why it waits for the office's OK (answer 90). */
  waitingOk: z.array(z.string()).nullable(),
  /** The confirm link a new client got (the quote that holds it), while the job waits on them. */
  confirm: z.object({ quoteId: z.uuid(), sentTo: z.string().nullable() }).nullable().optional(),
  /** The job's own switch for charging the client's card by itself. */
  autoPay: z.boolean(),
  /** Whether its invoices really get charged by themselves, and if not, why (canAutoCharge). */
  autoCharge: z.enum(["on", "job_off", "client_off", "no_card", "one_time"]),
  /** The client's card on file (owner, Oct 31: so the office does not call to ask for one by mistake). Left out for people
   * who do not see invoices. */
  card: z
    .object({ brand: z.string(), last4: z.string(), expMonth: z.number(), expYear: z.number(), byPhone: z.boolean() })
    .nullable()
    .optional(),
});
export type JobDetail = z.infer<typeof jobDetail>;

export const jobListQuery = listQuery.extend({
  q: z.string().trim().max(100).optional(),
  clientId: z.uuid().optional(),
  status: z.enum(["active", "ended", "all"]).default("active"),
  kind: z.enum(["repeating", "one-time", "all"]).default("all"),
});
export type JobListQuery = z.infer<typeof jobListQuery>;

// Visits ----------------------------------------------------------------------------------------------------------

export const VISIT_STATUSES = ["scheduled", "in_progress", "done", "skipped", "cancelled"] as const;
export type VisitStatus = (typeof VISIT_STATUSES)[number];

export const visitCard = z.object({
  /** The row's id, or "<job id>.<date>" while only computed. */
  ref: z.string(),
  id: z.uuid().nullable(),
  jobId: z.uuid(),
  jobNumber: z.number().nullable(),
  jobName: z.string(),
  clientId: z.uuid(),
  clientName: z.string(),
  date: z.string(),
  originalDate: z.string(),
  moved: z.boolean(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  status: z.enum(VISIT_STATUSES),
  address: z.string().nullable(),
  /** Where the property is on the map, when it is known. */
  lat: z.number().nullable(),
  lng: z.number().nullable(),
  crew: z.array(personChip),
  totalCents: z.number(),
  oneTime: z.boolean(),
  /** The client is a business (commercial work), for the schedule colors. */
  commercial: z.boolean(),
  /** The named route the job runs on (lawn), for the Route view and the month chips. */
  routeId: z.uuid().nullable(),
  /** The client has not signed the confirm link yet (jobs/confirm.ts): on the schedule, marked Not confirmed. */
  unconfirmed: z.boolean().optional(),
  /** A free visit to fix a complaint about a done visit (visits/fix.ts): no lines, no charge, no texts. */
  fix: z.boolean().optional(),
});
export type VisitCard = z.infer<typeof visitCard>;

export const checklistState = z.object({
  id: z.uuid(),
  name: z.string(),
  items: z.array(
    z.object({
      id: z.uuid(),
      label: z.string(),
      labelEs: z.string().nullable(),
      /** The room, for room-by-room lists (cleaning). Bedroom 2, Bathroom 1 for an item that repeats per room. */
      room: z.string().nullable(),
      /** Which bedroom or bathroom (1 for an item done once). Sent back with the tick. */
      copy: z.number(),
      /** Done before the crew can press Complete. */
      must: z.boolean(),
      done: z.boolean(),
      doneBy: z.string().nullable(),
    }),
  ),
  photosNeeded: z.number(),
  photosTaken: z.number(),
  /** What still keeps the crew from pressing Complete, in plain words. Empty when nothing does. */
  left: z.array(z.string()),
});

export const visitDetail = visitCard.extend({
  lines: z.array(lineRow),
  linesEdited: z.boolean(),
  client: z.object({ id: z.uuid(), name: z.string(), phone: z.string().nullable(), email: z.string().nullable() }),
  property: z.object({ id: z.uuid(), address: z.string(), summary: z.string() }).nullable(),
  officeNotes: z.string().nullable(),
  crewNotes: z.string().nullable(),
  timerStartedAt: z.string().nullable(),
  timerMinutes: z.number(),
  completedAt: z.string().nullable(),
  completedBy: z.string().nullable(),
  /** cash: the crew kept it. company: a check made out to the company, for the owner. check: an older answer, kept by the crew. */
  crewCollected: z.enum(["cash", "check", "company"]).nullable(),
  notesList: z.array(noteRow),
  photos: z.array(fileRow),
  checklist: checklistState.nullable(),
  repeating: z.boolean(),
  /** A job carried on over days: the day this one goes on to, the day it came from, and which part it is. */
  continuedTo: z.object({ ref: z.string(), date: z.string() }).nullable(),
  continuedFrom: z.object({ ref: z.string(), date: z.string() }).nullable(),
  part: z.number().nullable(),
  /** A fix visit: the done visit it fixes. */
  fixFor: z.object({ ref: z.string(), date: z.string() }).nullable(),
});
export type VisitDetail = z.infer<typeof visitDetail>;

/** Continue an unfinished job on a later day, or undo that while nothing was done on the extra day. */
export const visitContinueBody = z.union([z.object({ to: dateString }).strict(), z.object({ undo: z.literal(true) }).strict()]);

/** A free visit on a one-time job to fix a complaint about this done visit. */
export const visitFixBody = z
  .object({
    date: dateString,
    startTime: timeString.nullable().optional(),
    crew: z.array(z.uuid()).max(20).optional(),
    /** A blank note is no note. */
    crewNotes: z
      .string()
      .trim()
      .max(2000)
      .nullable()
      .optional()
      .transform((v) => (v === "" ? null : v)),
  })
  .strict();
export type VisitFixBody = z.infer<typeof visitFixBody>;

/** Lists of visits: the schedule (a date range, at most 62 days) or one client's or job's visits. */
export const visitsQuery = z
  .object({
    from: dateString,
    to: dateString,
    personId: z.uuid().optional(),
    /** Only visits with no one on them. ("false" in an address means false, not true.) */
    unassigned: z.union([z.boolean(), z.enum(["true", "false"]).transform((v) => v === "true")]).optional(),
    clientId: z.uuid().optional(),
    jobId: z.uuid().optional(),
  })
  .refine((q) => q.to >= q.from, { message: "The end is before the start.", path: ["to"] });
export type VisitsQuery = z.infer<typeof visitsQuery>;

export const scheduleAnswer = z.object({
  from: z.string(),
  to: z.string(),
  today: z.string(),
  visits: z.array(visitCard),
  /** role (owner, manager, dispatch or crew) keeps crew on the phone board on days they have no work. */
  people: z.array(personChip.extend({ role: z.string().optional() })),
});
export type ScheduleAnswer = z.infer<typeof scheduleAnswer>;

/**
 * My route (owner, Oct 31: Gabriel saw his schedule but no route): a crew member's own stops for a day in the Route view's
 * order, with the map line. on: routes are on for the company, so the crew board shows the Route button.
 */
export const myRouteAnswer = z.object({
  on: z.boolean(),
  visits: z.array(visitCard),
  path: routePathAnswer.nullable(),
});
export type MyRouteAnswer = z.infer<typeof myRouteAnswer>;

/** Move one visit, or change its times. The job's regular day stays. */
export const visitScheduleBody = z
  .object({
    date: dateString,
    startTime: timeString.nullable().optional(),
    endTime: timeString.nullable().optional(),
  })
  .refine((b) => !b.startTime || !b.endTime || b.endTime > b.startTime, {
    message: "The end is before the start.",
    path: ["endTime"],
  });

/** For repeating jobs: change only this visit, or this and every visit after it (the job changes from this date). */
export const SCOPES = ["this", "future"] as const;
export const visitLinesBody = z.object({
  lines: z.array(lineInput).max(50),
  scope: z.enum(SCOPES).default("this"),
  /** When the visit's paid invoice goes down: credit the client's account or don't (the same as Edit paid invoice). */
  credit: z.enum(["credit", "no_credit"]).optional(),
});
export const visitCrewBody = z.object({ crew: z.array(z.uuid()).max(20), scope: z.enum(SCOPES).default("this") });
/**
 * After a visit on a repeating job was saved: which of its settings every visit after it takes (Jobber's Apply visit
 * settings to future visits). repeats moves the repeat onto this visit's day.
 */
export const visitFutureBody = z
  .object({
    time: z.boolean().default(false),
    repeats: z.boolean().default(false),
    crew: z.boolean().default(false),
    lines: z.boolean().default(false),
  })
  .refine((b) => b.time || b.repeats || b.crew || b.lines, { message: "Pick at least one." });
export type VisitFutureBody = z.infer<typeof visitFutureBody>;
export const visitStatusBody = z.object({ status: z.enum(["skipped", "cancelled", "scheduled"]) });
export const visitCompleteBody = z.object({
  /** At brands with crew money: did the client pay the crew? cash is kept by the crew (it comes off their pay), company is a
   * company check the owner gets. No amount is ever typed by the crew. check is an older answer, still taken. */
  crewCollected: z.enum(["cash", "company", "check", "no"]).optional(),
});
export const visitTimerBody = z.object({ action: z.enum(["start", "stop"]) });
export const checklistTickBody = z.object({
  itemId: z.uuid(),
  done: z.boolean(),
  copy: z.number().int().min(1).max(20).default(1),
});

// Checklists ------------------------------------------------------------------------------------------------------

/** An item done once for each bedroom or each bathroom (old checklists.php bed and bath parts). */
export const CHECKLIST_REPEATS = ["bed", "bath"] as const;
export type ChecklistRepeat = (typeof CHECKLIST_REPEATS)[number];

/**
 * A checklist laid out for one home: an item that repeats comes once per bedroom or bathroom (a half bath counts as one),
 * named Bedroom 1, Bedroom 2... A home with no size on file gets each item once.
 */
export function expandChecklist<T extends { repeat: string | null; room: string | null }>(
  items: readonly T[],
  home: { beds: number | null; baths: number | null },
): (T & { copy: number; room: string | null })[] {
  const out: (T & { copy: number; room: string | null })[] = [];
  for (const i of items) {
    const n = i.repeat === "bed" ? (home.beds ?? 0) : i.repeat === "bath" ? Math.ceil(home.baths ?? 0) : 0;
    if (!i.repeat || n < 1) {
      out.push({ ...i, copy: 1 });
      continue;
    }
    const word = i.repeat === "bed" ? "Bedroom" : "Bathroom";
    for (let c = 1; c <= Math.min(n, 20); c++) out.push({ ...i, copy: c, room: `${word} ${String(c)}` });
  }
  return out;
}

export const checklistInput = z.object({
  name: z.string().trim().min(1).max(80),
  items: z
    .array(
      z.object({
        label: z.string().trim().min(1).max(200),
        labelEs: optionalText(200),
        room: optionalText(40),
        /** Done before the crew can press Complete. */
        must: z.boolean().default(false),
        /** Once for each bedroom (bed) or bathroom (bath) of the home. */
        repeat: z.enum(CHECKLIST_REPEATS).nullable().default(null),
      }),
    )
    .min(1, "Add at least one item.")
    .max(100),
  /** Photos the crew takes before they can press Complete. */
  photosNeeded: z.number().int().min(0).max(20).default(0),
});
export const checklistRow = z.object({
  id: z.uuid(),
  name: z.string(),
  photosNeeded: z.number(),
  items: z.array(
    z.object({
      id: z.uuid(),
      label: z.string(),
      labelEs: z.string().nullable(),
      room: z.string().nullable(),
      must: z.boolean(),
      repeat: z.enum(CHECKLIST_REPEATS).nullable(),
    }),
  ),
});
export type ChecklistRow = z.infer<typeof checklistRow>;

// Search ----------------------------------------------------------------------------------------------------------

export const searchQuery = z.object({ q: z.string().trim().min(1).max(100) });
export const searchAnswer = z.object({
  clients: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      phone: z.string().nullable(),
      address: z.string().nullable(),
      active: z.boolean(),
    }),
  ),
  jobs: z.array(z.object({ id: z.uuid(), number: z.number().nullable(), name: z.string(), clientName: z.string() })),
  visits: z.array(visitCard),
});
export type SearchAnswer = z.infer<typeof searchAnswer>;

// What a job costs (Billing, Expenses, Labor on the job page) -----------------------------------------------------

export const jobExpenseInput = z.object({
  item: z.string().trim().min(1, "Name the expense.").max(120),
  details: optionalText(500),
  spentOn: dateString,
  amountCents: z.number().int().min(1, "Type the amount.").max(10_000_000),
});
export type JobExpenseInput = z.infer<typeof jobExpenseInput>;
export const jobExpensePatch = jobExpenseInput.partial();
export type JobExpensePatch = z.infer<typeof jobExpensePatch>;

export const jobExpenseRow = z.object({
  id: z.uuid(),
  item: z.string(),
  details: z.string().nullable(),
  spentOn: z.string(),
  amountCents: z.number(),
  addedBy: z.string().nullable(),
});
export type JobExpenseRow = z.infer<typeof jobExpenseRow>;

export const jobLaborRow = z.object({
  visitId: z.uuid(),
  date: z.string(),
  person: personChip,
  minutes: z.number(),
  payCents: z.number(),
});
export type JobLaborRow = z.infer<typeof jobLaborRow>;

/** One visit's time in the job's time log: the timer from first start to last stop, or still running. */
export const jobTimeRow = z.object({
  visitId: z.uuid(),
  date: z.string(),
  done: z.boolean(),
  minutes: z.number(),
  /** Null when the time came from before the log (no clock times kept). */
  startedAt: z.string().nullable(),
  stoppedAt: z.string().nullable(),
  running: z.boolean(),
  startedBy: z.string().nullable(),
});
export type JobTimeRow = z.infer<typeof jobTimeRow>;

/** How long the job's visits take, start to finish (owner, 2026-11-03), from done visits with time on the timer. */
export const jobTime = z.object({
  avgMinutes: z.number().nullable(),
  timedVisits: z.number(),
  /** What the timed visits billed per hour on the timer; null when no prices are shown or no time is kept. */
  perHourCents: z.number().nullable(),
  log: z.array(jobTimeRow),
});
export type JobTime = z.infer<typeof jobTime>;

export const jobCosts = z.object({
  expenses: z.array(jobExpenseRow),
  expenseCents: z.number(),
  time: jobTime,
  /** Labor and profit show only to people who see payroll; null for the rest. */
  labor: z.array(jobLaborRow).nullable(),
  profit: z
    .object({
      /** Done visits counted. */
      visits: z.number(),
      revenueCents: z.number(),
      laborCents: z.number(),
      laborMinutes: z.number(),
      expenseCents: z.number(),
      profitCents: z.number(),
      marginPercent: z.number().nullable(),
    })
    .nullable(),
});
export type JobCosts = z.infer<typeof jobCosts>;
